<script lang="ts" setup>
import { EventBus } from "@/utils/EventBus";
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, useTemplateRef } from "vue";
import Dropdown from "./Dropdown.vue";
import MarkerDisplay from "./MarkerDisplay.vue";
import { useStore } from "@/store/Store";
import { MarkerCategory, Shape } from '@/shared/types/zod';
import { storeToRefs } from "pinia";
import Menu from "@icons/MaterialSymbolsMenu.svg";
import Close from "@icons/MaterialSymbolsClose.svg";
import ArrowIcon from "./icons/ArrowIcon.vue";

const props = defineProps<{
	open: boolean;
	slide: boolean;
	small: boolean;
	isZoomedToRoute: boolean;
	iframed: boolean;
}>();

const emit = defineEmits<{
	toggle: [open: boolean];
}>();

const store = useStore();

const dropOpen = ref(false);
const canScroll = ref(false);
const lastChange = ref(0);
const showLeftButton = ref(false);
const showRightButton = ref(false);

const scrollEl = useTemplateRef("scrollEl");
const left = useTemplateRef("left");
const right = useTemplateRef("right");

watch(() => props.open, () => {
	lastChange.value = Date.now();
	updateScroll();
});

watch(() => store.selectedRouteId, () => {
	nextTick(() => updateScroll());
});

const zoomableShapes = computed<Shape[]>(() => {
	return store.flatShapes?.filter(s =>
		s.properties.zoomable &&
		!(Array.isArray(s.properties.exclude) && s.properties.exclude.includes(store.selectedRouteId))
	) ?? [];
});

const mobile = computed(() => {
	return navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i) != null;
});

const fullscreenUrl = computed(() => {
	const url = new URL(window.location.href);
	const s = new URLSearchParams()
	if (url.searchParams.get("lang")) {
		s.set("lang", url.searchParams.get("lang")!);
	}
	url.search = s.toString();
	return url.href;
});

const smallIframed = computed<boolean>(() => {
	return props.small && props.iframed;
});

const { selectedRouteId, map, dropRoutes, uiText, categoryFilter } = storeToRefs(store)
const { localized } = store

const filteredMarkerCategories = computed((): MarkerCategory[] => {
	return store.markerCategories.filter(category => categoryFilter.value.includes(category.id));
});

const visibleFilteredMarkerCategories = computed(() => {
	return filteredMarkerCategories.value.length > 4
		? filteredMarkerCategories.value.slice(0, 3)
		: filteredMarkerCategories.value;
});

const hiddenFilteredCategoryCount = computed(() => {
	return Math.max(0, filteredMarkerCategories.value.length - visibleFilteredMarkerCategories.value.length);
});

// Direct access to store actions
// localized, setSelectedRouteId

function setOpen(open: boolean) {
	if (Date.now() - lastChange.value > 50) {
		emit("toggle", open);
	}
}

function zoomRoute() {
	EventBus.emit("zoomTo", "all");
}

function shapeClicked(shape: Shape) {
	// A shape sharing its slug with a route switches to that route. The route
	// zoom then fits the shape bounds (see Map.vue zoomTo "all").
	const slug = shape.properties.slug;
	const targetRoute = slug ? store.routes.find(r => r.properties.slug == slug) : undefined;
	if (targetRoute && targetRoute.id != store.selectedRouteId) {
		routeClicked(targetRoute.id as string);
		return;
	}
	EventBus.emit("zoomTo", shape);
}

function routeClicked(routeId: string | undefined) {
	if (!routeId) return;
	store.setSelectedRouteId(routeId);
	EventBus.emit("routeClicked");
}

function moreInfoClicked() {
	// if moreInfoContent is a single word, and a shape with the same name exists, zoom to it
	// instead of showing the popup.
	const moreInfoText = store.localized(store.map!.texts, 'moreInfoContent');
	if (moreInfoText && moreInfoText.indexOf(" ") == -1) {
		const f = store.featuresBySlug.get(moreInfoText);
		if (f) {
			EventBus.emit("zoomTo", f);
			return;
		}
	}
	EventBus.emit("moreInfo");
}

function paceClicked() {
	EventBus.emit("paceCalculator");
}

function clearCategoryFilter() {
	store.clearCategoryFilter();
}

function openFilterSidebar() {
	if (props.slide && !props.open) {
		setOpen(true);
	}
}


function scroll(direction: number) {
	const el = scrollEl.value;
	if (el) {
		el.scrollTo({
			left: el.scrollLeft + direction * el.clientWidth - 30,
			behavior: 'smooth'
		});
	}
}

function updateScroll() {
	const el = scrollEl.value;
	const leftButtonEl = left.value;
	const rightButtonEl = right.value;
	if (!el || !leftButtonEl || !rightButtonEl) return;

	const scrollLeft = el.scrollLeft;
	const scrollRight = el.scrollWidth - el.clientWidth - scrollLeft;

	showLeftButton.value = scrollLeft > 0.9 && !props.open && !mobile.value;
	showRightButton.value = scrollRight > 0.9 && !props.open && !mobile.value;
	canScroll.value = el.scrollWidth > el.clientWidth;
}

onMounted(() => {
	window.addEventListener("resize", updateScroll);
	updateScroll();
});

onBeforeUnmount(() => {
	window.removeEventListener("resize", updateScroll);
});
</script>



<template>
	<div v-if="map">
		<div class="top-bar no-scrollbars" :class="{ dropOpen, canScroll }" ref="scrollEl" @scroll="updateScroll">
			<button class="top-button shadow mr main" :class="{ close: open }" @click="setOpen(!open)"
				v-if="slide && !smallIframed" :aria-label="open ? 'Close' : undefined">
				<Close v-if="open" class="close-icon" />
				<template v-else>
					<Menu class="menu-icon" />
					{{ localized(map.texts, 'findInMap') || uiText.findInMap }}
				</template>
			</button>
			<dropdown class="top-button shadow mr" :options="dropRoutes" :value="selectedRouteId" @change="routeClicked"
				:selectOnClick="!isZoomedToRoute" v-if="dropRoutes.length && !smallIframed" @openChange="dropOpen = $event" />

			<!-- <button class="top-button shadow mr" @click="zoomRoute" v-if="currentRoute">
			{{ localized(currentRoute, "label") }}
		</button> -->
			<button v-if="!smallIframed" class="top-button shadow mr" v-for="shape in zoomableShapes" :key="shape.id"
				@click="shapeClicked(shape)">
				{{ localized(shape, "zoomableText") || 'Shape' }}
			</button>

			<button class="top-button shadow mr" v-if="localized(map.texts, 'moreInfoHeader') && !smallIframed"
				@click="moreInfoClicked">
				{{ localized(map.texts, 'moreInfoHeader') }}
			</button>

			<button class="top-button shadow mr"
				v-if="!smallIframed && store.currentRoute?.properties.paceCalculation.enabled" @click="paceClicked">
				Pace Calculator
			</button>

			<a v-if="iframed" :href="fullscreenUrl" target="_blank" class="top-button shadow __main fullscreen"
				:class="{ small }">
				{{ uiText.openMap }}
				<ArrowIcon direction="right" />
			</a>

			<!-- <button @click="$emit('locateMe')" class="top-button shadow mr">
				<div class="dot"></div>Min plats
			</button> -->

		</div>
		<div v-if="categoryFilter.length" class="category-filter shadow" :class="{ clickable: slide }"
			@click="openFilterSidebar">
			<span>{{ uiText.filterLabel }}:</span>
			<div class="category-filter-icons">
				<marker-display v-for="category in visibleFilteredMarkerCategories" :key="category.id"
					class="category-filter-icon" :icon="category.icon" :color="category.color" :square="true" />
				<span v-if="hiddenFilteredCategoryCount" class="category-filter-overflow">+{{ hiddenFilteredCategoryCount
				}}</span>
			</div>
			<button class="category-filter-clear" type="button" @click.stop="clearCategoryFilter">&times;</button>
		</div>
		<button class="top-button shadow left" :class="{ show: showLeftButton }" ref="left"
			@click="scroll(-1)">&lt;</button>
		<button class="top-button shadow right" :class="{ show: showRightButton }" ref="right"
			@click="scroll(1)">&gt;</button>
	</div>
</template>


<style scoped lang="scss">
.top-bar {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	// bottom: 0;
	height: 50px;
	align-items: flex-start;
	display: flex;
	flex-flow: row nowrap;
	padding: 8px;
	pointer-events: none;
	z-index: 1000000011;

	&.canScroll {
		touch-action: pan-x;
		pointer-events: inherit;
		overflow-x: auto;
		overflow-y: hidden;
	}

	&.dropOpen {
		pointer-events: none !important;
		height: unset;
		bottom: 0;
	}

	>* {
		pointer-events: initial;
		flex: 0 0 auto;
	}

	.menu-icon {
		width: 1rem;
		height: 1rem;
		margin: -.22rem;
		margin-right: -.1rem;
	}
}


.mr {
	margin-right: 6px;
}

.category-filter {
	position: absolute;
	top: 56px;
	left: 8px;
	display: flex;
	align-items: center;
	height: 34px;
	padding-left: 16px;
	border-radius: 30px;
	background: #fff;
	color: black;
	font-family: 'Poppins', sans-serif;
	font-size: 12px;
	font-weight: 500;
	z-index: 1000000010;
	pointer-events: auto;

	&.clickable {
		cursor: pointer;
	}

	@media (max-width: 768px) {
		top: 52px;
		height: 30px;
		padding-left: 12px;
	}
}

.category-filter-icons {
	display: flex;
	gap: 4px;
	margin-left: 8px;
	margin-right: 4px;
}

.category-filter-icon {
	width: 26px;
	height: 26px;
	border-radius: 4px;
	flex: 0 0 auto;

	@media (max-width: 768px) {
		width: 24px;
		height: 24px;
	}
}

.category-filter-overflow {
	display: grid;
	place-items: center;
	width: 26px;
	height: 26px;
	border-radius: 4px;
	background: #f0f0f0;
	color: #666;
	font-size: 12px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;

	@media (max-width: 768px) {
		width: 24px;
		height: 24px;
	}
}

.category-filter-clear {
	display: grid;
	place-items: center;
	width: 30px;
	height: 30px;
	margin-left: 2px;
	margin-right: 3px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: transparent;
	color: inherit;
	cursor: pointer;
	font-size: 20px;
	line-height: 1;

	@media (max-width: 768px) {
		width: 26px;
		height: 26px;
	}
}

.category-filter-clear:hover,
.category-filter-clear:focus-visible {
	background: #f0f0f0;
}

.location-buttons {
	border-radius: 30px;
	margin-bottom: 8px;
	display: flex;
	flex-flow: row nowrap;
}

.shadow {
	box-shadow: 0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%);
}

.top-button {
	flex: 0 0 auto;
	margin-bottom: 8px !important;
	height: 34px;

	pointer-events: initial;
	cursor: pointer;
	font-family: 'Poppins', sans-serif;
	font-weight: 500;
	font-size: 12px;
	background: white;
	// background: #ffffffee;
	border: none;
	/* border-radius: 30px; */
	padding: 8px 14px;
	pointer-events: auto;
	border-radius: 30px;
	color: black;

	@media (max-width: 768px) {
		height: 30px;
		padding: 6px 10px;
	}

	&.main {
		background-color: #333;
		color: #eee;
	}

	&.close {
		width: 34px;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;

		@media (max-width: 768px) {
			width: 30px;
		}
	}

	.close-icon {
		width: 20px;
		height: 20px;

		@media (max-width: 768px) {
			width: 18px;
			height: 18px;
		}
	}

	&.fullscreen {
		margin-left: auto;
		text-decoration: none;

		>svg {
			margin-left: 0px;
			margin-right: 0px;
		}


		&.small {
			height: unset;
			padding: .7rem 1.4rem;

			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			justify-content: space-between;
			width: 100%;

			>svg {
				margin-left: 10px;
				margin-right: -4px;
			}
		}
	}

	.dot {
		display: inline-block;
		width: 10px;
		height: 10px;
		border-radius: 10px;
		background-color: #1da1f2;
		margin-right: 5px;
	}

}

.left,
.right {
	width: 30px;
	border: solid 1px #999;
	display: none;

	z-index: 1000000011;
	margin-top: 8px;

	&.show {
		display: block;
	}


}

.left {
	position: absolute;
	left: 0;
	border-left: none;
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	padding-left: .4rem;
}

.right {
	position: absolute;
	right: 0;
	border-right: none;
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	padding-right: .4rem;
}
</style>